'use client';

import { Box, Flex, HStack, LucideIcon, Text, VStack } from '@skin-seoul/design-system';
import { useRegionCurrency } from '@skin-seoul/service';
import type { ComponentProps } from 'react';
import { useEffect, useMemo } from 'react';
import AIChoiceSection from './components/AIChoiceSection';
import AnalysisSummary from './components/AnalysisSummary/AnalysisSummary';
import MobileProductComparisionSummary from './components/MobileProductComparisionSummary';
import ProductFilterList from './components/ProductFilterList';
import { ProductCompareProvider, useProductCompareContext } from './context';
import { useInitializeSelectedProducts } from './hooks/useInitializeSelectedProducts';
import { useProductRecommendations } from './hooks/useProductRecommendations';
import { useRecentlyViewedProductIds } from './hooks/useRecentlyViewedProductIds';
import { ClientOnly, Condition, useImpressionRef } from '@skin-seoul/utils';
import RecentlyViewedSection from './components/RecentlyViewedSection';
import { trackAICompareImpression } from './utils/analytics';
const MAX_COMPARE_PRODUCTS = 10;
type ProductCompareSectionContentProps = {
  containerProps?: ComponentProps<typeof Flex>;
};
const ProductCompareSectionContent = ({
  containerProps
}: ProductCompareSectionContentProps) => {
  const {
    selectedProductId,
    setSelectedProductId,
    selectedFilter,
    currentProductId
  } = useProductCompareContext();
  const {
    inStockRecentlyViewedProductIds,
    hasInStockProducts
  } = useRecentlyViewedProductIds();
  const {
    currency
  } = useRegionCurrency();
  const impressionRef = useImpressionRef({
    onImpressionStart: trackAICompareImpression,
    impressionOnce: true,
    areaThreshold: 0.5
  });
  const limitedProductIds = useMemo(() => inStockRecentlyViewedProductIds.slice(0, MAX_COMPARE_PRODUCTS), [inStockRecentlyViewedProductIds]);
  const {
    recommendationsMap,
    recentlyViewedProducts,
    isFetching,
    isInitialLoading
  } = useProductRecommendations({
    productIds: limitedProductIds,
    currency,
    filter: selectedFilter
  });
  const recentlyViewedProductIds = Array.from(recommendationsMap.keys());
  const defaultBaseProductId = limitedProductIds[0] || 0;
  const selectedBaseProductId = selectedProductId.recentlyViewedProductId || defaultBaseProductId;
  const selectedRecommendations = recommendationsMap.get(selectedBaseProductId)?.recommendations;

  /**
   * Extract product IDs for recommendations, using parentProductId when available (for variants).
   * Note: These IDs may be parentProductIds, not actual productIds.
   * Components consuming these IDs (AnalysisSummary, etc.) must resolve the actual productId
   * by fetching recommendations again and finding the matching product.
   */
  const recommendationProductIds = useMemo(() => selectedRecommendations?.map(product => product.parentProductId ?? product.productId) ?? [], [selectedRecommendations]);
  useInitializeSelectedProducts({
    recentlyViewedProductIds,
    recommendationProductIds
  });
  useEffect(() => {
    setSelectedProductId(prev => ({
      ...prev,
      recommendationProductId: recommendationProductIds.length > 0 ? recommendationProductIds[0] : undefined
    }));
  }, [selectedFilter, selectedBaseProductId, recommendationProductIds, setSelectedProductId]);
  if (!hasInStockProducts) {
    return null;
  }
  const createProductClickHandler = (type: 'recentlyViewedProductId' | 'recommendationProductId') => (productId: number, productLink: string) => {
    const isSelected = selectedProductId[type] === productId;
    if (isSelected) {
      window.open(productLink, '_blank');
    } else {
      setSelectedProductId(prev => ({
        ...prev,
        [type]: productId
      }));
    }
  };
  const handleMobileRecentlyViewedClick = createProductClickHandler('recentlyViewedProductId');
  const handleMobileRecommendationClick = createProductClickHandler('recommendationProductId');

  /**
   * Product IDs for comparison.
   * Important: recommendationId may be a parentProductId (not actual productId).
   * Child components must resolve the actual productId by:
   * 1. Fetching recommendations using recentlyViewedId
   * 2. Finding the product where (parentProductId ?? productId) === recommendationId
   * 3. Using the found product's actual productId for API calls
   *
   * @see AnalysisSummary/AiInsight.tsx - Resolves productId for AI Insight API
   * @see AnalysisSummary/StatsSection.tsx - Resolves productId for stats comparison
   */
  const recentlyViewedId = selectedProductId.recentlyViewedProductId;
  const recommendationId = selectedProductId.recommendationProductId;
  const selectedRecentlyViewedProduct = recentlyViewedProducts.find(product => (product.parentProductId ?? product.productId) === recentlyViewedId);
  const isComparingWithCurrentProduct = selectedRecentlyViewedProduct?.productId === currentProductId || selectedRecentlyViewedProduct?.parentProductId === currentProductId;
  return <Flex ref={impressionRef} width="100%" background="white" direction="column" gap={4} {...containerProps} data-sentry-element="Flex" data-sentry-component="ProductCompareSectionContent" data-sentry-source-file="ProductCompareSection.tsx">
      <VStack width="100%" gap={6} alignItems="start" data-sentry-element="VStack" data-sentry-source-file="ProductCompareSection.tsx">
        <VStack paddingX={{
        base: 4,
        tablet: 0
      }} width="100%" gap={1} alignItems="start" data-sentry-element="VStack" data-sentry-source-file="ProductCompareSection.tsx">
          <HStack width="100%" gap={1} alignItems="center" data-sentry-element="HStack" data-sentry-source-file="ProductCompareSection.tsx">
            <Text textStyle="heading.h6.mobile" fontWeight="medium" color="neutral.700" as="h6" data-sentry-element="Text" data-sentry-source-file="ProductCompareSection.tsx">
              Find Your Best Match
            </Text>
            <Text px={2} py={1} textStyle="paragraph.xsmall" fontWeight="regular" color="neutral.600" bg="neutral.50" borderRadius={20} data-sentry-element="Text" data-sentry-source-file="ProductCompareSection.tsx">
              BETA
            </Text>
          </HStack>
          <Text textStyle="paragraph.small" fontWeight="regular" color="neutral.500" data-sentry-element="Text" data-sentry-source-file="ProductCompareSection.tsx">
            Powered by SkinSight™ AI to help you discover the right choice.
          </Text>
        </VStack>
        <ProductFilterList data-sentry-element="ProductFilterList" data-sentry-source-file="ProductCompareSection.tsx" />
        <Flex width="100%" gap={3} alignItems="stretch" flexDirection={{
        base: 'column',
        desktop: 'row'
      }} data-sentry-element="Flex" data-sentry-source-file="ProductCompareSection.tsx">
          <VStack desktop={{
          borderRadius: '12px',
          border: '1px solid',
          borderColor: 'neutral.75',
          p: 4
        }} gap={3} width={{
          base: '100%',
          desktop: 'fit-content'
        }} alignItems={{
          base: 'start',
          desktop: 'center'
        }} data-sentry-element="VStack" data-sentry-source-file="ProductCompareSection.tsx">
            <Text display={{
            base: 'block',
            desktop: 'none'
          }} textStyle="paragraph.xsmall" fontWeight="medium" color="neutral.400" paddingX={{
            base: 4,
            tablet: 0
          }} data-sentry-element="Text" data-sentry-source-file="ProductCompareSection.tsx">
              {isComparingWithCurrentProduct ? 'Compare with Current Product' : 'Compare With Recently Viewed'}
            </Text>
            <Text display={{
            base: 'none',
            desktop: 'block'
          }} textStyle="paragraph.xsmall" fontWeight="medium" color="neutral.400" paddingX={{
            base: 4,
            tablet: 0
          }} data-sentry-element="Text" data-sentry-source-file="ProductCompareSection.tsx">
              Recently Viewed
            </Text>

            <RecentlyViewedSection isFetching={isInitialLoading} selectedProductId={selectedProductId.recentlyViewedProductId} onProductClick={handleMobileRecentlyViewedClick} recentlyViewedProducts={recentlyViewedProducts} data-sentry-element="RecentlyViewedSection" data-sentry-source-file="ProductCompareSection.tsx" />
          </VStack>
          <Flex width="100%" css={{
          '@media (min-width: 1160px)': {
            borderRadius: '12px',
            background: 'linear-gradient(45deg, #7A3EFF 0%, #D6C3FF 100%)',
            padding: '1px',
            overflow: 'hidden'
          }
        }} data-sentry-element="Flex" data-sentry-source-file="ProductCompareSection.tsx">
            <VStack gap={3} width="100%" alignItems="start" desktop={{
            borderRadius: '11px',
            backgroundColor: 'primary.75',
            p: 4
          }} data-sentry-element="VStack" data-sentry-source-file="ProductCompareSection.tsx">
              <HStack width="100%" alignItems="center" gap={1} paddingX={{
              base: 4,
              tablet: 0
            }} justifyContent={{
              base: 'start',
              desktop: 'center'
            }} data-sentry-element="HStack" data-sentry-source-file="ProductCompareSection.tsx">
                <Text textStyle="paragraph.xsmall" fontWeight="medium" color="primary.600" data-sentry-element="Text" data-sentry-source-file="ProductCompareSection.tsx">
                  AI Choice
                </Text>
                <LucideIcon name="Sparkles" boxSize={4} color="primary.600" data-sentry-element="LucideIcon" data-sentry-source-file="ProductCompareSection.tsx" />
              </HStack>
              <Box width="100%" flex={1} data-sentry-element="Box" data-sentry-source-file="ProductCompareSection.tsx">
                <AIChoiceSection isFetching={isFetching} selectedRecommendations={selectedRecommendations} selectedProductId={selectedProductId.recommendationProductId} currency={currency} onProductClick={handleMobileRecommendationClick} data-sentry-element="AIChoiceSection" data-sentry-source-file="ProductCompareSection.tsx" />
              </Box>
            </VStack>
          </Flex>
        </Flex>
      </VStack>
      <Condition expression={Boolean(recentlyViewedId) && Boolean(recommendationId)} then={<VStack width="full" bg="neutral.50" borderRadius="12px" p={4} gap={4}>
            <Box width="full" display={{
        base: 'block',
        desktop: 'none'
      }}>
              <Condition expression={isFetching} then={<MobileProductComparisionSummary.Skeleton />} else={<MobileProductComparisionSummary orgProductId={recentlyViewedId} altProductId={recommendationId} />} />
            </Box>
            <AnalysisSummary orgProductId={recentlyViewedId} altProductId={recommendationId} isFetching={isFetching} />
          </VStack>} data-sentry-element="Condition" data-sentry-source-file="ProductCompareSection.tsx" />
    </Flex>;
};
type ProductCompareSectionProps = {
  containerProps?: ComponentProps<typeof Flex>;
  currentProductId?: number;
};
const ProductCompareSection = ({
  containerProps,
  currentProductId
}: ProductCompareSectionProps) => {
  return <ClientOnly data-sentry-element="ClientOnly" data-sentry-component="ProductCompareSection" data-sentry-source-file="ProductCompareSection.tsx">
      <ProductCompareProvider currentProductId={currentProductId} data-sentry-element="ProductCompareProvider" data-sentry-source-file="ProductCompareSection.tsx">
        <ProductCompareSectionContent containerProps={containerProps} data-sentry-element="ProductCompareSectionContent" data-sentry-source-file="ProductCompareSection.tsx" />
      </ProductCompareProvider>
    </ClientOnly>;
};
export default ProductCompareSection;